import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Spin, Empty, Skeleton } from "antd";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import moment from "moment";
import { Client } from "@stomp/stompjs";
import DOMPurify from "dompurify";
import { jwtDecode } from "jwt-decode";
import { getColorFromName, getInitials, formatPath } from "../../../../../../utils";
import { saveFile } from "../../../../../../features/save/saveSlice";
import axiosInstance from "../../../../../../instance";

const API = process.env.REACT_APP_API_BASE_URL;
const listUrl = `${API}/api/v2/project_chat/messages/list`;
const sendUrl = `${API}/api/v2/project_chat/messages/send`;
const markReadUrl = `${API}/api/v2/project_chat/messages/mark-read`;
const socketUrl = `${(API || "").replace(/^http/, "ws").replace(/\/+$/, "")}/ws-project-chat`;
const topic = (projectId) => `/topic/project-chat/${projectId}`;

const PILL_BASE =
  "inline-flex items-center rounded-md border px-2 py-1 font-[500] leading-[120%]";
const NOTE_PILL = "bg-[#FFF2DB] border-[#FFE5B4] text-[#AD7307] text-[.6875rem]";

const COMPOSER_TABS = [
  { key: "reply", label: "Chat" },
  { key: "note", label: "Internal Note" },
];

const COMPOSER_TOOLBAR_ID = "project-chat-toolbar";

function currentUserId() {
  try {
    return jwtDecode(localStorage.getItem("token") || "")?.usrId ?? null;
  } catch {
    return null;
  }
}

function mergeMessages(current, incoming) {
  const byId = new Map(current.map((m) => [m.id, m]));
  (incoming || []).forEach((m) => {
    if (m?.id == null) return;
    const existing = byId.get(m.id);
    byId.set(m.id, { ...existing, ...m, mine: existing?.mine === true || m.mine === true });
  });
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

function ChatMessage({ message }) {
  const isMine = message?.isMine;
  const isNote = message?.isInternalNote;
  const author = message?.author || "Unknown";

  return (
    <div className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}>
      <div className="flex items-center gap-x-[.62rem]">
        {!isMine && (
          <Avatar size={26} style={{ background: getColorFromName(author) }}>
            {getInitials(author)}
          </Avatar>
        )}
        <span className="font-interSans text-[.875rem] font-[500] leading-[120%] text-[#050505]">
          {isMine ? "You" : author}
        </span>
        {isNote && <span className={`${PILL_BASE} ${NOTE_PILL}`}>Internal Note</span>}
        {isMine && (
          <Avatar size={26} style={{ background: getColorFromName(author) }}>
            {getInitials(author)}
          </Avatar>
        )}
      </div>

      <div
        className={`mt-2 max-w-[85%] rounded-[.5rem] border p-3 ${
          isMine ? "rounded-tr-none" : "rounded-tl-none"
        } ${
          isNote
            ? "bg-[#FFF8EC] border-[#FFD27F]"
            : isMine
            ? "bg-[#E9F9FF] border-[#B4EAFF]"
            : "bg-white border-[#E6E6E6]"
        }`}
      >
        <div
          className="project-chat-body font-interSans text-[.75rem] font-[400] leading-[150%] text-[#050505]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message?.body || "") }}
        />

        {message?.attachmentUrl && (
          <a
            href={message.attachmentUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex items-center gap-x-1 text-[#0092CC] text-[.6875rem] font-[500] underline"
          >
            {formatPath(message.attachmentUrl)}
          </a>
        )}
      </div>

      <span className="mt-1.5 text-[#737373] text-[.6875rem] font-[500] leading-[120%]">
        {message?.createdAt
          ? moment(message.createdAt).format("DD-MM-YYYY HH:mm")
          : ""}
      </span>
    </div>
  );
}

const SKELETON_ROWS = [
  { mine: false, width: 230 },
  { mine: true, width: 170 },
  { mine: false, width: 265 },
  { mine: true, width: 195 },
];

function ChatSkeleton() {
  return (
    <>
      {SKELETON_ROWS.map((row, index) => (
        <div
          key={index}
          className={`flex flex-col ${row.mine ? "items-end" : "items-start"}`}
        >
          <div className="flex items-center gap-x-[.62rem]">
            {!row.mine && <Skeleton.Avatar active size={26} shape="circle" />}
            <Skeleton.Input
              active
              size="small"
              style={{ width: 90, minWidth: 90, height: 14 }}
            />
            {row.mine && <Skeleton.Avatar active size={26} shape="circle" />}
          </div>

          <div className="mt-2">
            <Skeleton.Input
              active
              style={{
                width: row.width,
                minWidth: row.width,
                height: 46,
                borderRadius: 8,
              }}
            />
          </div>

          <div className="mt-1.5">
            <Skeleton.Input
              active
              size="small"
              style={{ width: 70, minWidth: 70, height: 10 }}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function ProjectChat() {
  const dispatch = useDispatch();

  const {user} =  useSelector((state)=>state.auth)
console.log("user",user)
  const { activeObjOngoingProject } = useSelector((state) => ({
    activeObjOngoingProject: state.obj.activeObj["ongoingProject"],
  }));
  const projectId = activeObjOngoingProject?.projectId;

  const [rawMessages, setRawMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeComposer, setActiveComposer] = useState("reply");
  const [html, setHtml] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const messagesRef = useRef(null);
  const lastIdRef = useRef(null);

  const myId = useMemo(() => currentUserId(), []);

  useEffect(() => {
    lastIdRef.current = rawMessages.length
      ? rawMessages[rawMessages.length - 1].id
      : null;
  }, [rawMessages]);

  const messages = useMemo(
    () =>
      rawMessages.map((item) => ({
        id: item?.id,
        author: item?.senderName,
        body: item?.body,
        attachmentUrl: item?.attachmentUrl,
        isInternalNote: item?.internalNote === true,
        createdAt: item?.createdAt,
        isMine: item?.mine === true || (myId != null && item?.senderId === myId),
      })),
    [rawMessages, myId]
  );

  const markRead = useCallback(() => {
    if (!projectId) return;
    axiosInstance.post(markReadUrl, { projectId }).catch(() => {});
  }, [projectId]);

  const loadMessages = useCallback(
    async (afterId) => {
      if (!projectId) return;
      try {
        const res = await axiosInstance.post(listUrl, {
          projectId,
          ...(afterId != null ? { afterId, limit: 100 } : { limit: 50 }),
        });
        if (res?.data?.success) {
          const result = res?.data?.data?.result || [];
          setRawMessages((prev) =>
            afterId != null ? mergeMessages(prev, result) : mergeMessages([], result)
          );
          markRead();
        } else if (afterId == null) {
          toast.error(res?.data?.message || "Could not load the chat");
        }
      } catch (e) {
        if (afterId == null) toast.error("Could not load the chat");
      }
    },
    [projectId, markRead]
  );

  useEffect(() => {
    setLoading(true);
    setRawMessages([]);
    loadMessages().finally(() => setLoading(false));
  }, [loadMessages]);

  useEffect(() => {
    if (!projectId) return undefined;

    const client = new Client({
      brokerURL: socketUrl,
      connectHeaders: {
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        org_header: localStorage.getItem("selectedOrg") || "",
        county_header: localStorage.getItem("selectedCounty") || "",
      },
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(topic(projectId), (frame) => {
          try {
            const incoming = JSON.parse(frame.body);
            setRawMessages((prev) => mergeMessages(prev, [incoming]));
            markRead();
          } catch {
          }
        });
        if (lastIdRef.current != null) loadMessages(lastIdRef.current);
      },
      onStompError: (frame) => {
        console.warn("Project chat socket:", frame?.headers?.message);
      },
    });
    client.activate();

    return () => {
      client.deactivate();
    };
  }, [projectId, loadMessages, markRead]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages?.length]);

  const modules = useMemo(
    () => ({ toolbar: { container: `#${COMPOSER_TOOLBAR_ID}` } }),
    []
  );

  const isEmpty = !html?.replace(/<[^>]*>/g, "")?.trim() && !attachmentUrl;

  async function handleAttach(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB. Please upload a smaller file.");
      return;
    }

    setFileLoading(true);
    const formObject = new FormData();
    formObject.append("file", file);
    try {
      const res = await dispatch(saveFile(formObject));
      if (res?.payload?.success) {
        setAttachment(file);
        setAttachmentUrl(res?.payload?.targetUrl);
        toast.success(res?.payload?.message);
      } else {
        setAttachment(null);
        setAttachmentUrl("");
        toast.error(res?.payload?.message || "Upload failed");
      }
    } finally {
      setFileLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function handleCancelAttachment() {
    setAttachment(null);
    setAttachmentUrl("");
  }

  async function handleSend() {
    if (isEmpty || sending || !projectId) return;

    setSending(true);
    try {
      const res = await axiosInstance.post(sendUrl, {
        projectId,
        body: html?.replace(/<[^>]*>/g, "")?.trim() ? html : null,
        internalNote: activeComposer === "note",
        attachmentUrl: attachmentUrl || null,
      });

      if (res?.data?.success) {
        setHtml("");
        setAttachment(null);
        setAttachmentUrl("");
        const saved = res?.data?.data?.result;
        if (saved) setRawMessages((prev) => mergeMessages(prev, [saved]));
      } else {
        toast.error(res?.data?.message || "Could not send message");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not send message");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-col h-[600px]">
      <div
        ref={messagesRef}
        className="flex min-h-0 flex-1 flex-col gap-y-[1.25rem] overflow-y-auto pr-1"
      >
        {loading && !messages?.length ? (
          <ChatSkeleton />
        ) : messages?.length ? (
          messages?.map((message, index) => (
            <ChatMessage key={message?.id ?? index} message={message} />
          ))
        ) : (
          <div className="flex items-center justify-center h-full">
            <Empty description="No messages yet" />
          </div>
        )}
      </div>

      <div className="mt-[1.5rem] shrink-0 rounded-[.75rem] border border-[#E9E9E9]">
        <div className="flex items-center gap-x-1 border-b border-[#E9E9E9] px-2">
          {COMPOSER_TABS?.map((tab) => {
            const isActive = activeComposer === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveComposer(tab.key)}
                className={`cursor-pointer border-none bg-transparent px-3 py-3 text-[.8125rem] leading-[120%] ${
                  isActive
                    ? "text-[#0092CC] font-[500] border-b-2 border-b-[#0092CC]"
                    : "text-[#595959] font-[400]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <ReactQuill
          theme="snow"
          value={html}
          onChange={setHtml}
          modules={modules}
          placeholder={
            activeComposer === "note"
              ? "Add an internal note for the team..."
              : "Type your message here..."
          }
          className="project-chat-composer"
        />

        <div className="flex items-center justify-between border-t border-[#E9E9E9] px-3 py-2.5">
          <div className="flex items-center gap-x-1">
            <div id={COMPOSER_TOOLBAR_ID} className="project-chat-toolbar">
              <button type="button" className="ql-bold" />
              <button type="button" className="ql-italic" />
              <button type="button" className="ql-underline" />
              <button type="button" className="ql-list" value="bullet" />
              <button type="button" className="ql-link" />
            </div>

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              title="Attach a file"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-[#898781] hover:bg-[#F3F4F6]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <input
              ref={fileRef}
              type="file"
              className="!hidden"
              accept=".doc,.docx,.png,.jpeg,.jpg,.pdf"
              onChange={handleAttach}
            />
            {fileLoading ? (
              <Spin size="small" className="ml-1" />
            ) : (
              attachment && (
                <span className="ml-1 flex items-center gap-x-1 text-[#737373] text-[.6875rem] leading-[120%]">
                  {attachment.name}
                  <button
                    type="button"
                    onClick={handleCancelAttachment}
                    className="cursor-pointer border-none bg-transparent text-[#B81B0E]"
                  >
                    &times;
                  </button>
                </span>
              )
            )}
          </div>

          <button
            type="button"
            onClick={handleSend}
            disabled={isEmpty || sending || fileLoading}
            className={`rounded-[.375rem] px-4 py-2 text-[.8125rem] font-[500] text-white border-none ${
              isEmpty || sending || fileLoading
                ? "bg-[#9BD3EA] cursor-not-allowed"
                : "bg-[#0092CC] cursor-pointer"
            }`}
          >
            {sending ? <Spin size="small" /> : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectChat;
